import {
  Box,
  Button,
  Link,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";
import { Link as RouterLink } from "react-router-dom";

import { monospaceStyle } from "../../theme";
import {
  CategoryCapability,
  CategoryDetail,
  CategorySpecificationRange,
  CategoryTreeRow,
  CategoryTypicalHost,
} from "../../types/categoryDetail";
import { Page, ProductCard } from "../../types/productPlane";
import EntityLink from "../Detail/EntityLink";
import LabelValueGrid from "../Detail/LabelValueGrid";
import ShareBar from "../Detail/ShareBar";
import SourceMarks from "../Detail/SourceMarks";
import { columnHeader } from "../Detail/claimCells";
import { codeWords, formatNumber } from "../Detail/format";
import { specificationLabel } from "../Explore/exploreFilters";
import ProductResultCard from "../Explore/ProductResultCard";
import { exploreAddress } from "./CategoryHead";

// Whether the category has anything the overview shows
export const hasCategoryOverview = (category: CategoryDetail): boolean =>
  Boolean(category.definition) || category.typical_hosts.length > 0;

// A caption beneath a value, in the quieter colour
const Caption: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="caption" component="div" color="text.secondary">
    {children}
  </Typography>
);

// What the class is: a curated row with its rank, or a class found in
// articles with the curated row it is filed under
const KindOfClass: React.FC<{ category: CategoryDetail }> = ({ category }) => {
  if (category.curated) {
    return (
      <>
        {category.rank
          ? `Curated class, rank ${codeWords(category.rank)}`
          : "Curated class"}
      </>
    );
  }
  return (
    <>
      Found in articles
      {category.filed_under && (
        <>
          {" · filed under "}
          <EntityLink
            id={category.filed_under.id}
            type={category.filed_under.type}
            name={category.filed_under.name}
          />
        </>
      )}
    </>
  );
};

// One class the products are built into: the link, then how the relation
// came about beneath it, with its sources
const TypicalHost: React.FC<{ host: CategoryTypicalHost }> = ({ host }) => (
  <Box sx={{ mb: 0.5 }}>
    <EntityLink
      id={host.category.id}
      type={host.category.type}
      name={host.category.name}
    />
    <Caption>
      {[codeWords(host.relation), host.basis ? codeWords(host.basis) : null]
        .filter(Boolean)
        .join(" · ")}
      <SourceMarks sources={host.sources} />
    </Caption>
    {host.summary && <Caption>{host.summary}</Caption>}
  </Box>
);

export interface CategoryOverviewProps {
  category: CategoryDetail;
  // The category view's own overview: it also says what kind of class it
  // is and ends with the step into Explore with the class picked
  full?: boolean;
}

/**
 * The definition of the class and the classes its products are built into;
 * in full, what kind of class it is and the step into Explore as well.
 */
export const CategoryOverview: React.FC<CategoryOverviewProps> = ({
  category,
  full = false,
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
    <LabelValueGrid
      rows={[
        { label: "Definition", value: category.definition, wide: true },
        { label: "Kind", value: full && <KindOfClass category={category} /> },
        {
          label: "Typically built into",
          value:
            category.typical_hosts.length > 0 &&
            category.typical_hosts.map((host: CategoryTypicalHost) => (
              <TypicalHost
                key={`${host.category.id}:${host.relation}`}
                host={host}
              />
            )),
        },
      ]}
    />
    {full && (
      <Button
        component={RouterLink}
        to={exploreAddress(category.id)}
        variant="text"
        size="small"
        sx={{ alignSelf: "flex-start", px: 0, minWidth: 0 }}
      >
        Open in Explore with this class →
      </Button>
    )}
  </Box>
);

const numberCell = {
  align: "right" as const,
  sx: { ...monospaceStyle, whiteSpace: "nowrap" },
};

/**
 * The narrower classes as a table: each class a link, the products filed
 * under it or beneath it, and a chevron where it has narrower classes of
 * its own.
 */
export const NarrowerClasses: React.FC<{ rows: CategoryTreeRow[] }> = ({
  rows,
}) => (
  <Box sx={{ overflowX: "auto" }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={columnHeader}>Class</TableCell>
          <TableCell align="right" sx={columnHeader}>
            Products
          </TableCell>
          <TableCell sx={{ ...columnHeader, width: 24 }} />
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.category.id}>
            <TableCell>
              <EntityLink
                id={row.category.id}
                type={row.category.type}
                name={row.category.name}
              />
            </TableCell>
            <TableCell
              {...numberCell}
              aria-label={`${row.product_count} ${row.product_count === 1 ? "product" : "products"}`}
            >
              {row.product_count}
            </TableCell>
            <TableCell sx={{ color: "text.disabled", width: 24 }}>
              {row.has_narrower && (
                <Box
                  component="span"
                  role="img"
                  aria-label="Has narrower classes"
                  title="Has narrower classes"
                >
                  ›
                </Box>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

interface PagedListProps<T> {
  // The page the response holds
  page: Page<T>;
  // The name of the section, which the line begins with
  title: string;
  // Asks for the whole list
  onShowAll: () => void;
  children: React.ReactNode;
}

/**
 * A section whose list the response gives a page of: one line "Title · N of
 * M" as its heading and, while the page holds less than the whole, a text
 * button at the right that asks for the rest.
 */
function PagedList<T>({ page, title, onShowAll, children }: PagedListProps<T>) {
  const rest = page.total > page.items.length;
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 1.25,
        }}
      >
        <Typography variant="h6" component="h2">
          {title}{" "}
          <Box
            component="span"
            sx={{
              ...monospaceStyle,
              fontSize: "0.8125rem",
              fontWeight: 400,
              color: "text.secondary",
            }}
          >
            {`· ${page.items.length} of ${page.total}`}
          </Box>
        </Typography>
        {rest && (
          <Button size="small" variant="text" onClick={onShowAll}>
            Show all
          </Button>
        )}
      </Box>
      {children}
    </Box>
  );
}

interface TypicalCapabilitiesProps {
  page: Page<CategoryCapability>;
  onShowAll: () => void;
}

/**
 * The capabilities most products are said to have, as shares of the class.
 * A combined capability has no view of its own, so its name is no link.
 */
export const TypicalCapabilities: React.FC<TypicalCapabilitiesProps> = ({
  page,
  onShowAll,
}) => (
  <PagedList page={page} title="Typical capabilities" onShowAll={onShowAll}>
    <Box
      component="ul"
      sx={{
        listStyle: "none",
        m: 0,
        p: 0,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      {page.items.map((entry) => (
        <Box
          component="li"
          key={entry.capability.id}
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography variant="body2" component="span">
            {entry.capability.name}
          </Typography>
          <ShareBar
            count={entry.product_count}
            total={entry.out_of}
            noun="products"
          />
        </Box>
      ))}
    </Box>
  </PagedList>
);

interface SpecificationRangesProps {
  // The class the ranges are of; a key opens Explore with it picked
  categoryId: string;
  page: Page<CategorySpecificationRange>;
  onShowAll: () => void;
}

const SPECIFICATION_COLUMNS: { label: string; numeric: boolean }[] = [
  { label: "Key", numeric: false },
  { label: "Unit", numeric: false },
  { label: "Lowest", numeric: true },
  { label: "Median", numeric: true },
  { label: "Highest", numeric: true },
  { label: "Products", numeric: true },
];

/**
 * Lowest, median and highest value of each numeric specification. A key
 * opens Explore with the class picked and a range on the key.
 */
export const SpecificationRanges: React.FC<SpecificationRangesProps> = ({
  categoryId,
  page,
  onShowAll,
}) => (
  <PagedList page={page} title="Specification ranges" onShowAll={onShowAll}>
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {SPECIFICATION_COLUMNS.map((column) => (
              <TableCell
                key={column.label}
                align={column.numeric ? "right" : "left"}
                sx={columnHeader}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {page.items.map((range) => (
            <TableRow key={`${range.key}:${range.unit ?? ""}`}>
              <TableCell>
                <Link
                  component={RouterLink}
                  to={exploreAddress(categoryId, range.key)}
                  underline="hover"
                >
                  {specificationLabel(range.key)}
                </Link>
              </TableCell>
              <TableCell sx={{ ...monospaceStyle, color: "text.secondary" }}>
                {range.unit ?? ""}
              </TableCell>
              <TableCell {...numberCell}>{formatNumber(range.lowest)}</TableCell>
              <TableCell
                {...numberCell}
                sx={{ ...numberCell.sx, fontWeight: 600 }}
              >
                {formatNumber(range.middle)}
              </TableCell>
              <TableCell {...numberCell}>{formatNumber(range.highest)}</TableCell>
              <TableCell
                {...numberCell}
                sx={{ ...numberCell.sx, color: "text.secondary" }}
              >
                {range.product_count} of {range.out_of}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
    <Typography
      variant="caption"
      component="p"
      color="text.disabled"
      sx={{ mt: 1, mb: 0 }}
    >
      Median of each product's midpoint. A key opens Explore with this class
      and a range on it.
    </Typography>
  </PagedList>
);

interface CategoryProductsProps {
  page: Page<ProductCard>;
  onOpen: (product: ProductCard) => void;
  onPage: (offset: number) => void;
}

/**
 * The products of the class as cards, a page at a time: beneath the cards
 * which of them the page shows and in what order, and the pages to turn to.
 */
export const CategoryProducts: React.FC<CategoryProductsProps> = ({
  page,
  onOpen,
  onPage,
}) => {
  const first = page.total === 0 ? 0 : page.offset + 1;
  const last = page.offset + page.items.length;
  const size = page.limit > 0 ? page.limit : Math.max(page.items.length, 1);
  const pageCount = Math.ceil(page.total / size);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {page.items.map((product) => (
        <ProductResultCard key={product.id} product={product} onOpen={onOpen} />
      ))}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          <Box component="span" sx={monospaceStyle}>
            {`${first}–${last} of ${page.total}`}
          </Box>
          {" · most sourced first"}
        </Typography>
        {pageCount > 1 && (
          <Pagination
            aria-label="Product pages"
            count={pageCount}
            page={Math.floor(page.offset / size) + 1}
            onChange={(_event, pageNumber) => onPage((pageNumber - 1) * size)}
            size="small"
          />
        )}
      </Box>
    </Box>
  );
};
